import { useState, type Dispatch, type SetStateAction } from 'react'
import { useApp } from '../state/AppStore'
import { gameResult } from '../domain/derive'
import type { Game } from '../domain/types'

/**
 * Finishing a game.
 *
 * For a fixture-linked game there is nothing to enter: the score arrives from
 * PlayHQ and is looked up by playhqGameId whenever it is displayed. Asking the
 * scorer to type it as well would create a second source that could disagree
 * with the league's own record, which is exactly the disagreement this app
 * should never have to arbitrate.
 *
 * A manual game has no fixture behind it and therefore no source, so the
 * steppers remain -- there and only there. Without that exception a manual game
 * could never have a result, and every wins-vs-losses split in Stats would
 * quietly ignore it. Steppers rather than a keypad because the landscape
 * keyboard eats half the iPad.
 */
export function EndGame({
  game,
  opponentName,
  onCancel,
  onEnded,
}: {
  game: Game
  opponentName: string
  onCancel(): void
  onEnded(): void
}) {
  const { saveGame, setCurrentGameId } = useApp()
  const fromFixture = !!game.playhqGameId

  const [ours, setOurs] = useState(game.ourScore ?? 0)
  const [theirs, setTheirs] = useState(game.theirScore ?? 0)
  const [notes, setNotes] = useState(game.notes)
  const [saving, setSaving] = useState(false)

  const result = gameResult({ ourScore: ours, theirScore: theirs })

  const finish = async () => {
    setSaving(true)
    await saveGame({
      ...game,
      // Fixture games keep null scores on purpose: the snapshot is the source.
      ourScore: fromFixture ? null : ours,
      theirScore: fromFixture ? null : theirs,
      notes,
      status: 'final',
    })
    await setCurrentGameId(null)
    onEnded()
  }

  return (
    <div className="overlay">
      <div className="overlay__panel overlay__panel--narrow">
        <header className="overlay__header">
          <h2>{fromFixture ? 'Finish game' : 'Final score'}</h2>
          <button type="button" className="btn" onClick={onCancel}>
            Back to game
          </button>
        </header>

        {fromFixture ? (
          <div className="awaiting">
            <div className="awaiting__headline">
              {game.homeAway === 'home' ? 'vs' : '@'} {opponentName}
            </div>
            <p className="awaiting__lead">Awaiting result</p>
            <p className="muted">
              The score comes from PlayHQ once the league enters it, usually later tonight or the next
              day. Nothing to type in here.
            </p>
            <p className="muted small">
              Your stats are saved either way. If a result never appears, it can be set by hand from the
              admin.
            </p>
          </div>
        ) : (
          <div className="score">
            <Stepper label="Us" value={ours} onChange={setOurs} />
            <div className={'score__result score__result--' + (result ?? 'none')}>{result ?? '-'}</div>
            <Stepper label={opponentName} value={theirs} onChange={setTheirs} />
          </div>
        )}

        <label className="field">
          <span>Notes (optional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering" />
        </label>

        <button type="button" className="btn btn--primary btn--start" disabled={saving} onClick={finish}>
          {fromFixture ? 'Finish game' : 'Save game'}
        </button>
      </div>
    </div>
  )
}

function Stepper({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: Dispatch<SetStateAction<number>>
}) {
  // Functional updates, not `value + 1`: taps landing in the same React batch
  // would otherwise all read the same stale value and only one would count.
  return (
    <div className="stepper">
      <div className="stepper__label">{label}</div>
      <div className="stepper__value">{value}</div>
      <div className="stepper__buttons">
        <button type="button" className="btn stepper__btn" onClick={() => onChange((n) => Math.max(0, n - 1))}>
          −1
        </button>
        <button type="button" className="btn stepper__btn" onClick={() => onChange((n) => n + 1)}>
          +1
        </button>
        <button type="button" className="btn stepper__btn" onClick={() => onChange((n) => n + 10)}>
          +10
        </button>
      </div>
    </div>
  )
}
